import dayjs from "dayjs";
import { isEmptyNotesValue, NOTE_KEYS, valuesEqual } from "./notesMergeUtils";
import { formatCentralDateTime, parsePodSessionNotes } from "./notesUtils";
import { isUnclaimedProctor } from "./sessionUtils";

const IGNORED_INPUT_KEYS = new Set(["id", "__typename"]);

const FIELD_LABELS = {
  notes: "Notes",
  flag: "Flag",
  receipt: "Receipt",
  exam: "Exam",
  idCheck: "ID Check",
  roomCheck: "Room Check",
  personCheck: "Person Check",
  score: "Score",
  instructor: "Instructor",
  examCode: "Exam Code",
  confirmSubmission: "Confirm Submission",
  secondaryProctorNotes: "Secondary Proctor Notes",
  secondaryProctorLink: "Secondary Proctor Link",
  secondaryProctorName: "Secondary Proctor Name",
  startTime: "Start Time",
  endTime: "End Time",
  student_id: "Student ID",
  status: "Status",
  proctor: "Proctor",
  googleMeetLink: "Google Meet Link",
  verified_by: "ID Verified By",
  transferToProctor: "Transfer To Proctor",
  hasTesterJoined: "Tester Has Joined",
  integrationID: "Integration ID",
  testerName: "Tester Name",
  testerEmail: "Tester Email",
  testerPhone: "Tester Phone",
  organizationId: "Organization ID",
  organizationName: "Organization",
  podID: "Pod ID",
  fields: "Fields",
  isUrgent: "Urgent",
  claimedAt: "Claimed At",
  type: "Type",
  idPictureUrlPath: "ID Picture",
  selfiePictureUrlPath: "Selfie Picture",
  secondIdPictureUrlPath: "Second ID Picture",
};

function labelForKey(key) {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];

  const spaced = String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2");

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function parseHistoryInput(input) {
  let value = input;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (value == null) return {};
    if (typeof value === "object") {
      return Array.isArray(value) ? {} : value;
    }
    if (typeof value !== "string") return {};

    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }

  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  return {};
}

function notesStateFromInput(notes) {
  if (notes && typeof notes === "object") {
    return parsePodSessionNotes(JSON.stringify(notes));
  }

  return parsePodSessionNotes(notes);
}

function formatDisplayValue(value) {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value == null) return "(empty)";

  if (Array.isArray(value)) {
    if (value.length === 0) return "(empty)";

    return value
      .map((item) => {
        if (item && typeof item === "object" && "key" in item) {
          return `${item.key}: ${item.value ?? ""}`;
        }

        return typeof item === "object" ? JSON.stringify(item) : String(item);
      })
      .join("\n");
  }

  if (typeof value === "object") return JSON.stringify(value, null, 2);

  const text = String(value).trim();
  return text || "(empty)";
}

function formatScalarValue(key, value) {
  if (key === "proctor" && isUnclaimedProctor(value)) return "Unclaimed";
  return formatDisplayValue(value);
}

function scalarValuesEqual(key, previous, next) {
  if (
    key === "proctor" &&
    isUnclaimedProctor(previous) &&
    isUnclaimedProctor(next)
  ) {
    return true;
  }

  if (Object.is(previous, next)) return true;

  if (typeof previous === "boolean" || typeof next === "boolean") {
    return previous === next;
  }

  if (
    (previous != null && typeof previous === "object") ||
    (next != null && typeof next === "object")
  ) {
    return JSON.stringify(previous ?? null) === JSON.stringify(next ?? null);
  }

  return String(previous ?? "").trim() === String(next ?? "").trim();
}

function shouldShowBaselineNotesValue(value) {
  if (typeof value === "boolean") return value === true;
  return !isEmptyNotesValue(value);
}

function summarizeChanges(changes) {
  if (changes.length === 0) return "No field changes";

  const labels = changes.map((change) => change.label);
  const shown = labels.slice(0, 4);
  const extra = labels.length - shown.length;
  const text = shown.join(", ");

  return extra > 0 ? `${text} +${extra} more` : text;
}

export function formatHistoryTimestamp(timestamp) {
  const parsed = dayjs(timestamp);
  if (!parsed.isValid()) return String(timestamp || "Unknown time");
  return `${formatCentralDateTime(parsed.toDate())} CT`;
}

function actorName(actor) {
  const name = String(actor ?? "").trim();
  return name || "Unknown";
}

/**
 * Turn PodSessionHistory rows into newest-first saves.
 * Notes are stored as a full document on each save, so each row lists only
 * the note fields that differ from the previous save.
 */
export function buildHistoryEntries(records) {
  const chronological = (Array.isArray(records) ? records : [])
    .map((record, index) => ({ record, index }))
    .filter(({ record }) => record?.timestamp)
    .sort((a, b) => {
      const byTime = String(a.record.timestamp).localeCompare(
        String(b.record.timestamp),
      );
      if (byTime !== 0) return byTime;
      return a.index - b.index;
    });

  let previousNotes = null;
  const lastScalars = {};

  const entries = chronological.map(({ record }, entryIndex) => {
    const input = parseHistoryInput(record.input);
    const changes = [];
    let notesBaseline = false;

    if (Object.prototype.hasOwnProperty.call(input, "notes")) {
      const notes = notesStateFromInput(input.notes);

      if (previousNotes == null) {
        notesBaseline = true;
        NOTE_KEYS.forEach((key) => {
          if (!shouldShowBaselineNotesValue(notes[key])) return;
          changes.push({
            key: `notes.${key}`,
            label: labelForKey(key),
            value: formatDisplayValue(notes[key]),
          });
        });
      } else {
        NOTE_KEYS.forEach((key) => {
          if (valuesEqual(previousNotes[key], notes[key])) return;
          changes.push({
            key: `notes.${key}`,
            label: labelForKey(key),
            previous: formatDisplayValue(previousNotes[key]),
            value: formatDisplayValue(notes[key]),
          });
        });
      }

      previousNotes = notes;
    }

    Object.keys(input).forEach((key) => {
      if (IGNORED_INPUT_KEYS.has(key) || key === "notes") return;

      const nextValue = input[key];
      const previousValue = lastScalars[key];

      if (
        previousValue !== undefined &&
        scalarValuesEqual(key, previousValue, nextValue)
      ) {
        return;
      }

      const change = {
        key,
        label: labelForKey(key),
        value: formatScalarValue(key, nextValue),
      };

      if (previousValue !== undefined) {
        change.previous = formatScalarValue(key, previousValue);
      }

      changes.push(change);
      lastScalars[key] = nextValue;
    });

    return {
      key: `${record.timestamp}-${entryIndex}`,
      timestamp: record.timestamp,
      timeLabel: formatHistoryTimestamp(record.timestamp),
      actor: actorName(record.actor),
      summary: summarizeChanges(changes),
      notesBaseline,
      changes,
    };
  });

  return entries.reverse();
}
